import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as MatchManagamentApi from "../../services/match.managament.service";
import { useNavigate, useParams } from "react-router-dom";
import { formateDate, formateDateWithMonth, handleServerValidations } from "../../../../utils/commonfunction";
import Breadcrums from "../../common/breadcrumbs";
import { Placeholder } from "rsuite";
import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";

const MatchManagamentDetails = () => {
  const params = useParams();
  const { t } = useTranslation();
  const [showdefault, setShowDefault] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [rawEvents, setRawEvents] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [matchAnalysisData, setMatchAnalysisData] = useState(null);
  const [matchAnalysisLoading, setMatchAnalysisLoading] = useState(false);
  const [searchPlayer, setSearchPlayer] = useState("");
  const [searchEvent, setSearchEvent] = useState("");
  const [selectedPlayerMath, setSelectedPlayerMath] = useState(null);
  const navigate = useNavigate();

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Match Management"), url: "/admin/match-management/match/list/1" },
    { title: t("btn_view"), url: "" },
  ];

  useEffect(() => {
    setIsLoading(true);
    MatchManagamentApi.Details(params.id)
      .then((response) => {
        setShowDefault(response?.data || {});
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [params.id]);

  useEffect(() => {
    if (params.id) {
      setMatchAnalysisLoading(true);
      MatchManagamentApi.GetMatchAnalysis(params.id)
        .then((response) => {
          setMatchAnalysisData(response?.data || null);
        })
        .catch((err) => {
          console.error("Failed to load match analysis data:", err);
        })
        .finally(() => {
          setMatchAnalysisLoading(false);
        });
    }
  }, [params.id]);

  useEffect(() => {
    if (showdefault?.homeTeamData?.id) {
      setInsightsLoading(true);
      setEventsLoading(true);
      
      MatchManagamentApi.GetTeamInsights(showdefault.homeTeamData.id, params.id)
        .then((response) => {
          setInsights(response?.data || null);
        })
        .catch((err) => {
          console.error("Failed to load team insights:", err);
        })
        .finally(() => {
          setInsightsLoading(false);
        });

      MatchManagamentApi.GetRawEvents(showdefault.homeTeamData.id, params.id)
        .then((response) => {
          setRawEvents(response?.data || []);
        })
        .catch((err) => {
          console.error("Failed to load raw events:", err);
        })
        .finally(() => {
          setEventsLoading(false);
        });
    }
  }, [showdefault?.homeTeamData?.id, params.id]);




  const handleExportPDF = () => {
    if (!rawEvents || rawEvents.length === 0) {
      Swal.fire({
        icon: "info",
        text: "No raw events available for this match.",
        ...SWAL_SETTINGS,
      });
      return;
    }

    try {
      const doc = new jsPDF();

      // Header Branding
      doc.setFontSize(22);
      doc.setTextColor(32, 128, 120); // #208078
      doc.text("mNetrics", 14, 20);

      doc.setFontSize(14);
      doc.setTextColor(44, 42, 99); // #2c2a63
      doc.text("Match Raw Event Logs Report", 14, 30);

      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Generated: ${moment().format("MMMM Do YYYY, h:mm:ss a")}`, 14, 37);

      doc.setDrawColor(230);
      doc.line(14, 42, 196, 42);

      // Match Details Meta Section
      const matchTitle = showdefault?.title || "Match Report";
      const homeTeam = showdefault?.homeTeamData?.name || "Home Team";
      const awayTeam = showdefault?.awayTeam ? showdefault.awayTeam.replace(/_/g, " ") : "Away Team";
      const dateTime = showdefault?.date
        ? moment(`${showdefault.date} ${showdefault.time || ""}`).format("DD MMM YYYY, HH:mm")
        : "N/A";
      const status = showdefault?.matchStatus || showdefault?.status || "N/A";

      doc.setFontSize(10);
      doc.setTextColor(50);
      doc.setFont(undefined, 'bold');
      doc.text(`Match Title:`, 14, 50);
      doc.setFont(undefined, 'normal');
      doc.text(`${matchTitle}`, 42, 50);

      doc.setFont(undefined, 'bold');
      doc.text(`Teams:`, 14, 56);
      doc.setFont(undefined, 'normal');
      doc.text(`${homeTeam} (Home) vs ${awayTeam} (Away)`, 42, 56);

      doc.setFont(undefined, 'bold');
      doc.text(`Date & Time:`, 14, 62);
      doc.setFont(undefined, 'normal');
      doc.text(`${dateTime}`, 42, 62);

      doc.setFont(undefined, 'bold');
      doc.text(`Status:`, 120, 62);
      doc.setFont(undefined, 'normal');
      doc.text(`${status}`, 140, 62);

      doc.setFont(undefined, 'bold');
      doc.text(`Total Events:`, 120, 56);
      doc.setFont(undefined, 'normal');
      doc.text(`${rawEvents.length}`, 145, 56);

      // Table Data
      const SUB_ACTIONS = ["TECHNICAL_SUBSTITUTION", "SUBSTITUTION", "TECHNICAL-SUBSTITUTION", "CHANGE_PLAYER_POSITION", "CHANGE-PLAYER-POSITION"];
      const tableColumn = ["Q", "Time", "Player", "Team", "Position", "Action", "Pts"];
      const tableRows = rawEvents.map(e => {
        const act = (e.actionType || "").toUpperCase();
        const isSub = SUB_ACTIONS.includes(act);
        return [
          `Q${e.quarter || 1}`,
          e.createdAt ? moment(e.createdAt).format("HH:mm:ss") : "-",
          e.playerName || "-",
          e.teamName || "-",
          e.position || "-",
          (e.actionType || "").replace(/-/g, " "),
          isSub ? 0 : (e.points ?? 0)
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 68,
        theme: 'striped',
        headStyles: { fillColor: [32, 128, 120], fontSize: 9, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2.5 },
        alternateRowStyles: { fillColor: [248, 252, 251] },
        margin: { top: 20 },
        didDrawPage: (data) => {
          const str = `Page ${doc.internal.getNumberOfPages()}`;
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(str, data.settings.margin.left, doc.internal.pageSize.height - 10);
        }
      });

      const sanitizeTitle = matchTitle.replace(/[^a-zA-Z0-9]/g, "_");
      const fileName = `mNetrics_RawEvents_${sanitizeTitle}_${moment().format("YYYYMMDD")}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Error generating raw events PDF:", error);
      Swal.fire({
        icon: "error",
        text: "Failed to export raw events PDF.",
        ...SWAL_SETTINGS,
      });
    }
  };

  const handleExportChainsPDF = () => {
    if (!rawEvents || rawEvents.length === 0) {
      Swal.fire({
        icon: "info",
        text: "No raw events available for this match.",
        ...SWAL_SETTINGS,
      });
      return;
    }

    const chainEvents = rawEvents.filter(e => e.chainId && e.chainId !== "NONE" && String(e.chainId).trim() !== "");

    if (!chainEvents || chainEvents.length === 0) {
      Swal.fire({
        icon: "info",
        text: "No chain events recorded for this match.",
        ...SWAL_SETTINGS,
      });
      return;
    }

    try {
      const doc = new jsPDF();

      // Header Branding
      doc.setFontSize(22);
      doc.setTextColor(32, 128, 120); // #208078
      doc.text("mNetrics", 14, 20);

      doc.setFontSize(14);
      doc.setTextColor(44, 42, 99); // #2c2a63
      doc.text("Match Possession Chains Report (Chains Only)", 14, 30);

      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Generated: ${moment().format("MMMM Do YYYY, h:mm:ss a")}`, 14, 37);

      doc.setDrawColor(230);
      doc.line(14, 42, 196, 42);

      // Match Details Meta Section
      const matchTitle = showdefault?.title || "Match Report";
      const homeTeam = showdefault?.homeTeamData?.name || "Home Team";
      const awayTeam = showdefault?.awayTeam ? showdefault.awayTeam.replace(/_/g, " ") : "Away Team";
      const dateTime = showdefault?.date
        ? moment(`${showdefault.date} ${showdefault.time || ""}`).format("DD MMM YYYY, HH:mm")
        : "N/A";
      const status = showdefault?.matchStatus || "N/A";

      const uniqueChainIds = [...new Set(chainEvents.map(e => e.chainId))];

      doc.setFontSize(10);
      doc.setTextColor(50);
      doc.setFont(undefined, 'bold');
      doc.text(`Match Title:`, 14, 50);
      doc.setFont(undefined, 'normal');
      doc.text(`${matchTitle}`, 42, 50);

      doc.setFont(undefined, 'bold');
      doc.text(`Teams:`, 14, 56);
      doc.setFont(undefined, 'normal');
      doc.text(`${homeTeam} (Home) vs ${awayTeam} (Away)`, 42, 56);

      doc.setFont(undefined, 'bold');
      doc.text(`Date & Time:`, 14, 62);
      doc.setFont(undefined, 'normal');
      doc.text(`${dateTime}`, 42, 62);

      doc.setFont(undefined, 'bold');
      doc.text(`Status:`, 120, 62);
      doc.setFont(undefined, 'normal');
      doc.text(`${status}`, 140, 62);

      doc.setFont(undefined, 'bold');
      doc.text(`Total Chains:`, 120, 50);
      doc.setFont(undefined, 'normal');
      doc.text(`${uniqueChainIds.length}`, 145, 50);

      doc.setFont(undefined, 'bold');
      doc.text(`Chain Events:`, 120, 56);
      doc.setFont(undefined, 'normal');
      doc.text(`${chainEvents.length}`, 145, 56);

      // Table Data
      const SUB_ACTIONS = ["TECHNICAL_SUBSTITUTION", "SUBSTITUTION", "TECHNICAL-SUBSTITUTION", "CHANGE_PLAYER_POSITION", "CHANGE-PLAYER-POSITION"];
      const tableColumn = ["Chain ID", "Q", "Time", "Player", "Team", "Position", "Action", "Pts"];
      const tableRows = chainEvents.map(e => {
        const act = (e.actionType || "").toUpperCase();
        const isSub = SUB_ACTIONS.includes(act);
        return [
          e.chainId || "-",
          `Q${e.quarter || 1}`,
          e.createdAt ? moment(e.createdAt).format("HH:mm:ss") : "-",
          e.playerName || "-",
          e.teamName || "-",
          e.position || "-",
          (e.actionType || "").replace(/-/g, " "),
          isSub ? 0 : (e.points ?? 0)
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 68,
        theme: 'striped',
        headStyles: { fillColor: [111, 66, 193], fontSize: 9, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2.5 },
        alternateRowStyles: { fillColor: [248, 245, 252] },
        margin: { top: 20 },
        didDrawPage: (data) => {
          const str = `Page ${doc.internal.getNumberOfPages()}`;
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(str, data.settings.margin.left, doc.internal.pageSize.height - 10);
        }
      });

      const sanitizeTitle = matchTitle.replace(/[^a-zA-Z0-9]/g, "_");
      const fileName = `mNetrics_ChainsOnly_${sanitizeTitle}_${moment().format("YYYYMMDD")}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Error generating chains PDF:", error);
      Swal.fire({
        icon: "error",
        text: "Failed to export chains PDF.",
        ...SWAL_SETTINGS,
      });
    }
  };

  const formateDateLocal = (date) => {
    if (!date || date === "N/A") return "N/A";
    const d = new Date(date);
    const time = d
      .toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).toUpperCase();

    const formattedDate = d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    return `${time} (${formattedDate})`;
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="row row-sm">
        <div className="col-lg-12">
          <div className="card custom-card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom flex-wrap gap-2">
                <h6 className="main-content-label mb-0" style={{ fontSize: "18px" }}>
                  Match Management View
                </h6>
                <ul className="nav nav-tabs border-0" role="tablist">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link border-0 fw-bold px-4 py-2 ${activeTab === "details" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{ cursor: "pointer", background: "none", fontSize: "14px" }}
                      onClick={() => setActiveTab("details")}
                    >
                      <i className="ri-information-line me-1"></i> Match Details Overview
                    </button>
                  </li>
                  {/* <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link border-0 fw-bold px-4 py-2 ${activeTab === "analysis" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{ cursor: "pointer", background: "none", fontSize: "14px" }}
                      onClick={() => setActiveTab("analysis")}
                    >
                      <i className="ri-bar-chart-box-line me-1"></i> Match Analysis
                      <span className="badge bg-success-light text-success ms-2 px-2 py-1 rounded-pill" style={{ fontSize: "10px" }}>Live Insights & Formulas</span>
                    </button>
                  </li> */}
                </ul>
              </div>

              {activeTab === "details" && (
                isLoading ? (
                  <Placeholder.Paragraph active rows={7} />
                ) : showdefault && Object.keys(showdefault).length > 0 ? (
                  <>
                  {/* ================= BASIC MATCH INFO ================= */}
                  <table className="table table-bordered table-hover">
                    <tbody>
                      <tr>
                        <th>{t("label_title")}</th>
                        <td>{showdefault && showdefault?.title ? showdefault?.title : "N/A"}</td>
                      </tr>
                      <tr>
                        <th>{t("label_matches_status")}</th>
                        <td>
                          {showdefault?.matchStatus ? (
                            <span
                              className={
                                "badge badge-pill fw-bold text-uppercase px-3 py-2 " +
                                {
                                  live: "badge-danger-light",
                                  finished: "badge-warning-light",
                                  upcoming: "badge-success-light",
                                }[showdefault.matchStatus.toLowerCase()] || "badge-secondary"
                              }
                            >
                              {showdefault.matchStatus}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th>{t("label_date")}</th>
                        <td>{showdefault && showdefault?.date ? showdefault?.date : "N/A"}</td>
                      </tr>


                      <tr>
                        <th>{t("label_start_time")}</th>
                        <td>{formateDateLocal(showdefault?.matchStartTime || "N/A")}</td>
                      </tr>

                      <tr>
                        <th>{t("label_end_time")}</th>
                        <td>{formateDateLocal(showdefault?.matchEndTime || "N/A")}</td>
                      </tr>

                      <tr>
                        <th>{t("label_home_team")}</th>
                        <td>{showdefault && showdefault?.homeTeamData?.name ? showdefault?.homeTeamData?.name : "N/A"}</td>
                      </tr>
                      <tr>
                        <th>{t("label_away_team")}</th>
                        <td>{showdefault && showdefault?.awayTeam ? showdefault?.awayTeam.replace(/_/g, " ") : "N/A"}</td>
                      </tr>
                      <tr>
                        <th>{t("label_venue_title")}</th>
                        <td>{showdefault && showdefault?.venueTitle ? showdefault?.venueTitle : "N/A"}</td>
                      </tr>
                      <tr>
                        <th>{t("label_venue_location")}</th>
                        <td>{showdefault && showdefault?.venueLocation ? showdefault?.venueLocation : "N/A"}</td>
                      </tr>
                      <tr>
                        <th>{t("label_venue_quarter_length")}</th>
                        <td>{showdefault && showdefault?.quarterLength ? showdefault?.quarterLength : "N/A"}</td>
                      </tr>
                      <tr>
                        <th>{t("label_venue_total_quarter")}</th>
                        <td>{showdefault && showdefault?.totalQuarters ? showdefault?.totalQuarters : "N/A"}</td>
                      </tr>
                      <tr>
                        <th>{t("label_created_at")}</th>
                        <td>{formateDate(showdefault && showdefault?.createdAt ? showdefault?.createdAt : "N/A")}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* ================= CLUB ADMIN IN VIEW ================= */}
                  <h6 className="main-content-label mb-3">
                    {t("label_club_school")}
                  </h6>
                  <table className="table table-bordered table-hover mt-4">
                    <thead>
                      <tr>
                        <th>{t("label_name")}</th>
                        <th>{t("label_email")}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {showdefault?.clubAdmin ? (
                        <tr>
                          <td>{showdefault && showdefault?.clubAdmin && showdefault?.clubAdmin?.name ? showdefault?.clubAdmin?.name : "N/A"}</td>
                          <td>{showdefault && showdefault?.clubAdmin && showdefault?.clubAdmin?.email ? showdefault?.clubAdmin?.email : "N/A"}</td>
                        </tr>
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center">
                            {t("msg_no_records")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* ================= HOME PLAYERS INFO ================= */}
                  <h6 className="main-content-label mb-3">{t("label_home_players_info")}</h6>
                  <table className="table table-bordered table-hover mt-4">
                    <thead>
                      <tr>
                        <th>{t("label_position")}</th>
                        <th>{t("label_name")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showdefault?.homePlayersInfo ? (
                        Object.entries(showdefault.homePlayersInfo).map(
                          ([pos, player], i) => (
                            <tr key={i}>
                              <td>{pos}</td>
                              <td>{player?.name || "N/A"}</td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center">N/A</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* ================= HOME IMPACT PLAYERS ================= */}
                  <h6 className="main-content-label mb-3">{t("label_home_impact_players")}</h6>
                  <table className="table table-bordered table-hover mt-4">
                    <thead>
                      <tr>
                        <th>{t("label_name")}</th>
                        <th>{t("label_profile_image")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showdefault?.homeImpactPlayers ? (
                        Object.entries(showdefault.homeImpactPlayers).map(
                          ([pos, player], i) => (
                            <tr key={i}>
                              <td>
                                {player?.name ? player.name : "N/A"}
                              </td>

                              {/* ✅ IMAGE TD FIX */}
                              <td>
                                {player?.profile_image ? (
                                  <img
                                    src={player.profile_image}
                                    alt="profile"
                                    width={40}
                                    height={40}
                                    style={{
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  "N/A"
                                )}
                              </td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center">
                            N/A
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* ================= IMPACT PLAYERS ================= */}
                  <h6 className="main-content-label mb-3">{t("label_impact_players")}</h6>
                  <table className="table table-bordered table-hover mt-4">
                    <thead>
                      <tr>
                        <th>{t("label_name")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showdefault?.homeImpactPlayers ? (
                        Object.entries(showdefault.homeImpactPlayers).map(
                          ([pos, player], i) => (
                            <tr key={i}>
                              <td>{player && player?.name ? player?.name : "N/A"}</td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center">N/A</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* ================= AWAY PLAYERS INFO ================= */}
                  <h6 className="main-content-label mb-3">{t("label_away_player_info")}</h6>
                  <table className="table table-bordered table-hover mt-4">
                    <thead>
                      <tr>
                        <th>{t("label_position")}</th>
                        <th>{t("label_name")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showdefault?.awayPlayersInfo ? (
                        Object.entries(showdefault.awayPlayersInfo).map(
                          ([pos, player], i) => (
                            <tr key={i}>
                              <td>{pos}</td>
                              <td>{player?.name || "N/A"}</td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center">N/A</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* ================= AWAY IMPACT PLAYERS ================= */}
                  <h6 className="main-content-label mb-3">{t("label_away_impact_players")}</h6>
                  <table className="table table-bordered table-hover mt-4">
                    <thead>
                      <tr>
                        <th>{t("label_name")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showdefault?.awayImpactPlayers ? (
                        Object.entries(showdefault.awayImpactPlayers).map(
                          ([pos, away], i) => (
                            <tr key={i}>
                              <td>{away && away?.name ? away?.name : "N/A"}</td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center">N/A</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* break between quarter */}
                  <h6 className="main-content-label mb-3">
                    {t("label_break_between_quarter")}
                  </h6>
                  <table className="table table-bordered table-hover mt-4">
                    <thead>
                      <tr>
                        <th>{t("label_quarter")}</th>
                        <th>{t("label_length")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showdefault?.breakBetweenQuarters &&
                        Object.keys(showdefault.breakBetweenQuarters).length > 0 ? (
                        Object.entries(showdefault.breakBetweenQuarters).map(
                          ([quarter, value], index) => (
                            <tr key={index}>
                              <td>{quarter}</td>
                              <td>{value ?? "N/A"}</td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center">
                            N/A
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
 
                  {/* ================= MATCH REPORT STATISTICS ================= */}
                  {/* <h6 className="main-content-label mb-3 mt-5">Match Report Statistics</h6>
                  {insightsLoading ? (
                    <Placeholder.Paragraph active rows={5} className="mt-4" />
                  ) : (
                    <table className="table table-bordered table-hover mt-4">
                      <thead>
                        <tr className="bg-light">
                          <th>Statistic</th>
                          <th>{showdefault?.homeTeamData?.name || "Home Team"} (Home)</th>
                          <th>{showdefault?.awayTeam ? showdefault.awayTeam.replace(/_/g, " ") : "Away Team"} (Away)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Goals</strong></td>
                          <td>{insights?.homeTeamStats?.goals ?? 0}</td>
                          <td>{insights?.awayTeamStats?.goals ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Missed Shots</strong></td>
                          <td>{insights?.homeTeamStats?.missed_shots ?? 0}</td>
                          <td>{insights?.awayTeamStats?.missed_shots ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Total Shots</strong></td>
                          <td>{insights?.homeTeamStats?.total_shots ?? 0}</td>
                          <td>{insights?.awayTeamStats?.total_shots ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Shooting Efficiency</strong></td>
                          <td>
                            {insights?.homeTeamStats?.total_shots > 0
                              ? ((insights.homeTeamStats.goals / insights.homeTeamStats.total_shots) * 100).toFixed(1) + "%"
                              : "0.0%"}
                          </td>
                          <td>
                            {insights?.awayTeamStats?.total_shots > 0
                              ? ((insights.awayTeamStats.goals / insights.awayTeamStats.total_shots) * 100).toFixed(1) + "%"
                              : "0.0%"}
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Interceptions</strong></td>
                          <td>{insights?.homeTeamStats?.interceptions ?? 0}</td>
                          <td>{insights?.awayTeamStats?.interceptions ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Tips</strong></td>
                          <td>{insights?.homeTeamStats?.tips ?? 0}</td>
                          <td>{insights?.awayTeamStats?.tips ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Rebounds</strong></td>
                          <td>{insights?.homeTeamStats?.rebounds ?? 0}</td>
                          <td>{insights?.awayTeamStats?.rebounds ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Assists</strong></td>
                          <td>{insights?.homeTeamStats?.assists ?? 0}</td>
                          <td>{insights?.awayTeamStats?.assists ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Feeds</strong></td>
                          <td>{insights?.homeTeamStats?.feeds ?? 0}</td>
                          <td>{insights?.awayTeamStats?.feeds ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Feed Success</strong></td>
                          <td>{insights?.homeTeamStats?.feeds_success ?? 0}</td>
                          <td>{insights?.awayTeamStats?.feeds_success ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Feed Success Rate</strong></td>
                          <td>
                            {insights?.homeTeamStats?.feeds > 0
                              ? ((insights.homeTeamStats.feeds_success / insights.homeTeamStats.feeds) * 100).toFixed(1) + "%"
                              : "0.0%"}
                          </td>
                          <td>
                            {insights?.awayTeamStats?.feeds > 0
                              ? ((insights.awayTeamStats.feeds_success / insights.awayTeamStats.feeds) * 100).toFixed(1) + "%"
                              : "0.0%"}
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Turnovers Won</strong></td>
                          <td>{insights?.homeTeamStats?.turnovers_won ?? 0}</td>
                          <td>{insights?.awayTeamStats?.turnovers_won ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Turnovers Lost</strong></td>
                          <td>{insights?.homeTeamStats?.turnovers_lost ?? 0}</td>
                          <td>{insights?.awayTeamStats?.turnovers_lost ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Net Turnovers</strong></td>
                          <td>
                            {(insights?.homeTeamStats?.turnovers_won ?? 0) - (insights?.homeTeamStats?.turnovers_lost ?? 0)}
                          </td>
                          <td>
                            {(insights?.awayTeamStats?.turnovers_won ?? 0) - (insights?.awayTeamStats?.turnovers_lost ?? 0)}
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Gain</strong></td>
                          <td>{insights?.homeTeamStats?.gain ?? 0}</td>
                          <td>{insights?.awayTeamStats?.gain ?? 0}</td>
                        </tr>
                        <tr>
                          <td><strong>Errors</strong></td>
                          <td>{insights?.homeTeamStats?.errors ?? 0}</td>
                          <td>{insights?.awayTeamStats?.errors ?? 0}</td>
                        </tr>
                      </tbody>
                    </table>
                  )} */}

                  {/* ================= RAW EVENT LOGS ================= */}
                  {/* <div className="d-flex justify-content-between align-items-center mb-3 mt-5">
                    <h6 className="main-content-label mb-0">Raw Event Logs</h6>
                    {rawEvents && rawEvents.length > 0 && (
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm text-white"
                          style={{ backgroundColor: "#208078", fontWeight: 600 }}
                          onClick={handleExportPDF}
                        >
                          <i className="ri-file-pdf-fill me-1"></i> Export PDF
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm text-white"
                          style={{ backgroundColor: "#6f42c1", fontWeight: 600 }}
                          onClick={handleExportChainsPDF}
                        >
                          <i className="ri-links-fill me-1"></i> Export Chains Only PDF
                        </button>
                      </div>
                    )}
                  </div> */}
                  {/* {eventsLoading ? (
                    <Placeholder.Paragraph active rows={5} className="mt-4" />
                  ) : (
                    <div style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid #e8e8f7", borderRadius: "4px", marginTop: "20px" }}>
                      <table className="table table-bordered table-hover mb-0">
                        <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1 }}>
                          <tr>
                            <th>Quarter</th>
                            <th>Time</th>
                            <th>Player</th>
                            <th>Team</th>
                            <th>Position</th>
                            <th>Action Type</th>
                            <th>Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rawEvents && rawEvents.length > 0 ? (
                            rawEvents.map((event, i) => (
                              <tr key={i}>
                                <td>Q{event.quarter}</td>
                                <td>
                                  {event.createdAt 
                                    ? new Date(event.createdAt).toLocaleTimeString("en-IN", { 
                                        hour: "2-digit", 
                                        minute: "2-digit", 
                                        second: "2-digit", 
                                        hour12: true 
                                      }).toUpperCase() 
                                    : "-"}
                                </td>
                                <td>{event.playerName || "-"}</td>
                                <td>{event.teamName || "-"}</td>
                                <td>{event.position || "-"}</td>
                                <td>{event.actionType ? event.actionType.replace(/-/g, " ") : "-"}</td>
                                <td>{event.points ?? 0}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">No events recorded for this match.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )} */}

                  {/* ================= HOW INSIGHTS ARE CALCULATED ================= */}
                  {/* {(() => {
                    const weights = insights?.weights;
                    const defInterception = weights?.DEFENDERS?.INTERCEPTION !== undefined ? Number(weights.DEFENDERS.INTERCEPTION) : 1.5;
                    const defTip = weights?.DEFENDERS?.TIP !== undefined ? Number(weights.DEFENDERS.TIP) : 0.75;
                    const defRebound = weights?.DEFENDERS?.DEFENSIVE_REBOUND !== undefined ? Number(weights.DEFENDERS.DEFENSIVE_REBOUND) : 0.75;

                    const defInterceptionScore = (defInterception * 2);
                    const defTipScore = (defTip * 2);
                    const defReboundScore = (defRebound * 2);

                    const goalWeight = weights?.MIDCOURT?.GOAL !== undefined ? Math.abs(Number(weights.MIDCOURT.GOAL)) : 1;
                    const interceptionWeight = weights?.DEFENDERS?.INTERCEPTION !== undefined ? Math.abs(Number(weights.DEFENDERS.INTERCEPTION)) : 1.5;
                    const turnoverWeight = weights?.MIDCOURT?.TURNOVER_LOST !== undefined ? Math.abs(Number(weights.MIDCOURT.TURNOVER_LOST)) : 1;

                    return (
                      <div className="calculation-basis mt-5" style={{ backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "8px", borderLeft: "4px solid #208078" }}>
                        <h6 className="main-content-label mb-3" style={{ color: "#208078", textTransform: "none" }}>How Insights are Calculated</h6>
                        <ul style={{ paddingLeft: "20px", marginBottom: 0, fontSize: "13px", color: "#555" }}>
                          <li style={{ marginBottom: "8px" }}><strong>Shooting Efficiency:</strong> Calculated as <code>(Total Goals / Total Shots) * 100</code>. Only actions of type <code>GOAL</code> and <code>SHOT</code> (or <code>MISSED_SHOT</code>) are considered.</li>
                          <li style={{ marginBottom: "8px" }}><strong>Defensive Impact:</strong> Weighted sum of defensive actions: <code>(Interceptions * {defInterceptionScore}) + (Blocks/Tips * {defTipScore}) + (Rebounds * {defReboundScore})</code>.</li>
                          <li style={{ marginBottom: "8px" }}><strong>Safe Play %:</strong> Measure of ball security: <code>(1 - (Turnovers / (Goals + Turnovers))) * 100</code>. Turnovers include <code>TURNOVER_LOST</code> and <code>SELF_ERROR</code>.</li>
                          <li style={{ marginBottom: "8px" }}><strong>Momentum Tracking:</strong> A dynamic score that increases with <code>GOALS</code> (+{goalWeight}) and <code>INTERCEPTIONS</code> (+{interceptionWeight}), and decreases with <code>GOAL_AGAINST</code> (-{goalWeight}) and <code>TURNOVERS</code> (-{turnoverWeight}).</li>
                          <li style={{ marginBottom: "8px" }}><strong>Conversion Chains:</strong> Tracks outcomes starting from a <code>CENTER_PASS</code>. A successful chain ends in a <code>GOAL</code> without lost possession.</li>
                          <li style={{ marginBottom: 0 }}><strong>Quarterly Reports:</strong> Aggregates all actions by the <code>quarter</code> field recorded at the time of the event.</li>
                        </ul>
                      </div>
                    );
                  })()} */}

                  {/* BACK BUTTON */}
                  <div className="mt-5">
                    <button
                      className="btn ripple btn-dark"
                      type="button"
                      onClick={() => navigate(-1)}
                    >
                      <i className="ace-icon-solid ion-arrow-return-left bigger-110 mx-1"></i>
                      {t("btn_back")}
                    </button>
                  </div>
                </>
              ) : (
                <Placeholder.Paragraph active rows={7} />
              ))}

              {activeTab === "analysis" && (
                <div className="match-analysis-wrapper">
                  {matchAnalysisLoading ? (
                    <Placeholder.Paragraph active rows={10} />
                  ) : matchAnalysisData ? (
                    <>
                      {/* ================= 1. TEAM INSIGHTS SUMMARY CARDS & TABLE ================= */}
                      <div className="row mb-4">
                        <div className="col-12 mb-3">
                          <h6 className="main-content-label text-primary" style={{ fontSize: "16px" }}>
                            <i className="ri-pie-chart-2-line me-2"></i> 1. Team Insights Data Summary
                          </h6>
                          <p className="text-muted small">Aggregated performance metrics comparing Home ({matchAnalysisData?.matchDetails?.homeTeam?.name}) vs Away ({matchAnalysisData?.matchDetails?.awayTeam?.name}) teams.</p>
                        </div>

                        {/* Summary metric cards */}
                        <div className="col-md-3 col-sm-6 mb-3">
                          <div className="card shadow-sm border-0 bg-light p-3 rounded-3 text-center h-100">
                            <span className="text-muted small fw-bold">TOTAL MATCH SCORE</span>
                            <h3 className="fw-bold mt-2 text-dark mb-0">
                              {matchAnalysisData?.teamInsights?.homeTeamStats?.score ?? 0} - {matchAnalysisData?.teamInsights?.awayTeamStats?.score ?? 0}
                            </h3>
                            <span className="small text-secondary mt-1">
                              {matchAnalysisData?.matchDetails?.homeTeam?.name} vs {matchAnalysisData?.matchDetails?.awayTeam?.name}
                            </span>
                          </div>
                        </div>

                        <div className="col-md-3 col-sm-6 mb-3">
                          <div className="card shadow-sm border-0 bg-light p-3 rounded-3 text-center h-100">
                            <span className="text-muted small fw-bold">SHOOTING EFFICIENCY</span>
                            <h3 className="fw-bold mt-2 text-primary mb-0">
                              {matchAnalysisData?.teamInsights?.homeTeamStats?.total_shots > 0 
                                ? ((matchAnalysisData.teamInsights.homeTeamStats.goals / matchAnalysisData.teamInsights.homeTeamStats.total_shots) * 100).toFixed(1) + "%" 
                                : "0.0%"} 
                              {" / "}
                              {matchAnalysisData?.teamInsights?.awayTeamStats?.total_shots > 0 
                                ? ((matchAnalysisData.teamInsights.awayTeamStats.goals / matchAnalysisData.teamInsights.awayTeamStats.total_shots) * 100).toFixed(1) + "%" 
                                : "0.0%"}
                            </h3>
                            <span className="small text-muted mt-1">Home % vs Away %</span>
                          </div>
                        </div>

                        <div className="col-md-3 col-sm-6 mb-3">
                          <div className="card shadow-sm border-0 bg-light p-3 rounded-3 text-center h-100">
                            <span className="text-muted small fw-bold">PIR RATING TOTAL</span>
                            <h3 className="fw-bold mt-2 text-success mb-0">
                              {matchAnalysisData?.teamInsights?.homeTeamStats?.pir_total ?? 0} / {matchAnalysisData?.teamInsights?.awayTeamStats?.pir_total ?? 0}
                            </h3>
                            <span className="small text-muted mt-1">Player Impact Rating Total</span>
                          </div>
                        </div>

                        <div className="col-md-3 col-sm-6 mb-3">
                          <div className="card shadow-sm border-0 bg-light p-3 rounded-3 text-center h-100">
                            <span className="text-muted small fw-bold">INTERCEPTIONS</span>
                            <h3 className="fw-bold mt-2 text-info mb-0">
                              {matchAnalysisData?.teamInsights?.homeTeamStats?.interceptions ?? 0} vs {matchAnalysisData?.teamInsights?.awayTeamStats?.interceptions ?? 0}
                            </h3>
                            <span className="small text-muted mt-1">Defensive Gains</span>
                          </div>
                        </div>
                      </div>

                      {/* Detailed Team Insights Table */}
                      <div className="table-responsive mb-5 border rounded-3 overflow-hidden">
                        <table className="table table-striped table-hover mb-0">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th>Team Insight Metric</th>
                              <th>{matchAnalysisData?.matchDetails?.homeTeam?.name} (Home)</th>
                              <th>{matchAnalysisData?.matchDetails?.awayTeam?.name} (Away)</th>
                              <th>Formula & Difference</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><strong>Goals Scored</strong></td>
                              <td className="fw-bold">{matchAnalysisData?.teamInsights?.homeTeamStats?.goals ?? 0}</td>
                              <td className="fw-bold">{matchAnalysisData?.teamInsights?.awayTeamStats?.goals ?? 0}</td>
                              <td>
                                <span className={`badge ${((matchAnalysisData?.teamInsights?.homeTeamStats?.goals ?? 0) >= (matchAnalysisData?.teamInsights?.awayTeamStats?.goals ?? 0)) ? 'bg-success' : 'bg-warning'} px-2 py-1`}>
                                  {Math.abs((matchAnalysisData?.teamInsights?.homeTeamStats?.goals ?? 0) - (matchAnalysisData?.teamInsights?.awayTeamStats?.goals ?? 0))} Goal Diff
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td><strong>Missed Shots</strong></td>
                              <td>{matchAnalysisData?.teamInsights?.homeTeamStats?.missed_shots ?? 0}</td>
                              <td>{matchAnalysisData?.teamInsights?.awayTeamStats?.missed_shots ?? 0}</td>
                              <td>-</td>
                            </tr>
                            <tr>
                              <td><strong>Total Shots Attempted</strong></td>
                              <td>{matchAnalysisData?.teamInsights?.homeTeamStats?.total_shots ?? 0}</td>
                              <td>{matchAnalysisData?.teamInsights?.awayTeamStats?.total_shots ?? 0}</td>
                              <td>Goals + Missed Shots</td>
                            </tr>
                            <tr>
                              <td><strong>Shooting Accuracy %</strong></td>
                              <td className="fw-bold">
                                {matchAnalysisData?.teamInsights?.homeTeamStats?.total_shots > 0
                                  ? ((matchAnalysisData.teamInsights.homeTeamStats.goals / matchAnalysisData.teamInsights.homeTeamStats.total_shots) * 100).toFixed(1) + "%"
                                  : "0.0%"}
                              </td>
                              <td className="fw-bold">
                                {matchAnalysisData?.teamInsights?.awayTeamStats?.total_shots > 0
                                  ? ((matchAnalysisData.teamInsights.awayTeamStats.goals / matchAnalysisData.teamInsights.awayTeamStats.total_shots) * 100).toFixed(1) + "%"
                                  : "0.0%"}
                              </td>
                              <td>Formula: <code>(Goals / Total Shots) * 100</code></td>
                            </tr>
                            <tr>
                              <td><strong>Assists</strong></td>
                              <td>{matchAnalysisData?.teamInsights?.homeTeamStats?.assists ?? 0}</td>
                              <td>{matchAnalysisData?.teamInsights?.awayTeamStats?.assists ?? 0}</td>
                              <td>Passes leading to Goals</td>
                            </tr>
                            <tr>
                              <td><strong>Total Feeds</strong></td>
                              <td>{matchAnalysisData?.teamInsights?.homeTeamStats?.feeds ?? 0}</td>
                              <td>{matchAnalysisData?.teamInsights?.awayTeamStats?.feeds ?? 0}</td>
                              <td>-</td>
                            </tr>
                            <tr>
                              <td><strong>Successful Feeds</strong></td>
                              <td>{matchAnalysisData?.teamInsights?.homeTeamStats?.feeds_success ?? 0}</td>
                              <td>{matchAnalysisData?.teamInsights?.awayTeamStats?.feeds_success ?? 0}</td>
                              <td>Circle Entries Completed</td>
                            </tr>
                            <tr>
                              <td><strong>Feed Success Rate %</strong></td>
                              <td>
                                {(() => {
                                  const homeFs = matchAnalysisData?.teamInsights?.homeTeamStats?.feeds_success || 0;
                                  const homeTotalF = (matchAnalysisData?.teamInsights?.homeTeamStats?.feeds || 0) + homeFs;
                                  return homeTotalF > 0 ? ((homeFs / homeTotalF) * 100).toFixed(1) + "%" : "0.0%";
                                })()}
                              </td>
                              <td>
                                {(() => {
                                  const awayFs = matchAnalysisData?.teamInsights?.awayTeamStats?.feeds_success || 0;
                                  const awayTotalF = (matchAnalysisData?.teamInsights?.awayTeamStats?.feeds || 0) + awayFs;
                                  return awayTotalF > 0 ? ((awayFs / awayTotalF) * 100).toFixed(1) + "%" : "0.0%";
                                })()}
                              </td>
                              <td>Formula: <code>[Feed Success / (Feeds + Feed Success)] * 100</code></td>
                            </tr>
                            <tr>
                              <td><strong>Interceptions</strong></td>
                              <td>{matchAnalysisData?.teamInsights?.homeTeamStats?.interceptions ?? 0}</td>
                              <td>{matchAnalysisData?.teamInsights?.awayTeamStats?.interceptions ?? 0}</td>
                              <td>Defensive Turnovers Won</td>
                            </tr>
                            <tr>
                              <td><strong>Tips / Deflections</strong></td>
                              <td>{matchAnalysisData?.teamInsights?.homeTeamStats?.tips ?? 0}</td>
                              <td>{matchAnalysisData?.teamInsights?.awayTeamStats?.tips ?? 0}</td>
                              <td>Defensive Touches</td>
                            </tr>
                            <tr>
                              <td><strong>Rebounds</strong></td>
                              <td>{matchAnalysisData?.teamInsights?.homeTeamStats?.rebounds ?? 0}</td>
                              <td>{matchAnalysisData?.teamInsights?.awayTeamStats?.rebounds ?? 0}</td>
                              <td>Offensive + Defensive Rebounds</td>
                            </tr>
                            <tr>
                              <td><strong>Turnovers Won</strong></td>
                              <td className="text-success fw-bold">{matchAnalysisData?.teamInsights?.homeTeamStats?.turnovers_won ?? 0}</td>
                              <td className="text-success fw-bold">{matchAnalysisData?.teamInsights?.awayTeamStats?.turnovers_won ?? 0}</td>
                              <td>Possessions Gained</td>
                            </tr>
                            <tr>
                              <td><strong>Turnovers Lost</strong></td>
                              <td className="text-danger">{matchAnalysisData?.teamInsights?.homeTeamStats?.turnovers_lost ?? 0}</td>
                              <td className="text-danger">{matchAnalysisData?.teamInsights?.awayTeamStats?.turnovers_lost ?? 0}</td>
                              <td>Possessions Conceded</td>
                            </tr>
                            <tr>
                              <td><strong>Net Turnovers</strong></td>
                              <td className="fw-bold">
                                {(matchAnalysisData?.teamInsights?.homeTeamStats?.turnovers_won ?? 0) - (matchAnalysisData?.teamInsights?.homeTeamStats?.turnovers_lost ?? 0)}
                              </td>
                              <td className="fw-bold">
                                {(matchAnalysisData?.teamInsights?.awayTeamStats?.turnovers_won ?? 0) - (matchAnalysisData?.teamInsights?.awayTeamStats?.turnovers_lost ?? 0)}
                              </td>
                              <td>Formula: <code>Turnovers Won - Turnovers Lost</code></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* ================= 2. PLAYER REPORT DATA ================= */}
                      <div className="mb-5">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <div>
                            <h6 className="main-content-label text-primary" style={{ fontSize: "16px" }}>
                              <i className="ri-user-star-line me-2"></i> 2. Player Report Data
                            </h6>
                            <p className="text-muted small mb-0">Detailed individual match performance metrics and PIR for all players.</p>
                          </div>
                          <div style={{ width: "250px" }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Search player, team, or position..."
                              value={searchPlayer}
                              onChange={(e) => setSearchPlayer(e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="table-responsive border rounded-3 overflow-hidden">
                          <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="bg-light text-dark fw-bold">
                              <tr>
                                <th>Player Name</th>
                                <th>Team</th>
                                <th>Pos</th>
                                <th>PIR</th>
                                <th>Goals</th>
                                <th>Shots</th>
                                <th>Accuracy %</th>
                                <th>Assists</th>
                                <th>Feeds</th>
                                <th>Feed %</th>
                                <th>Interceptions</th>
                                <th>Tips</th>
                                <th>Rebounds</th>
                                <th>TO Won</th>
                                <th>TO Lost</th>
                                <th>Net TO</th>
                                <th>Safe Play %</th>
                                <th>Formula Justification</th>
                              </tr>
                            </thead>
                            <tbody>
                              {matchAnalysisData?.playerReports && matchAnalysisData.playerReports.length > 0 ? (
                                matchAnalysisData.playerReports
                                  .filter(p => 
                                    !searchPlayer || 
                                    p.playerName.toLowerCase().includes(searchPlayer.toLowerCase()) || 
                                    p.teamName.toLowerCase().includes(searchPlayer.toLowerCase()) ||
                                    p.position.toLowerCase().includes(searchPlayer.toLowerCase())
                                  )
                                  .map((p, idx) => (
                                    <tr key={idx} className={selectedPlayerMath?.playerId === p.playerId ? "table-primary" : ""}>
                                      <td className="fw-bold">{p.playerName}</td>
                                      <td>
                                        <span className={`badge ${p.side === 'HOME' ? 'bg-primary-light text-primary' : 'bg-info-light text-info'} px-2 py-1`}>
                                          {p.teamName}
                                        </span>
                                      </td>
                                      <td><span className="badge bg-secondary px-2 py-1">{p.position}</span></td>
                                      <td className="fw-bold text-success" style={{ fontSize: "14px" }}>{p.pir}</td>
                                      <td className="fw-bold text-dark">{p.goals}</td>
                                      <td>{p.totalShots}</td>
                                      <td>{p.shootingEfficiencyPct}%</td>
                                      <td>{p.assists}</td>
                                      <td>{p.feeds}</td>
                                      <td>{p.feedSuccessRatePct}%</td>
                                      <td>{p.interceptions}</td>
                                      <td>{p.tips}</td>
                                      <td>{p.rebounds}</td>
                                      <td className="text-success">{p.turnoversWon}</td>
                                      <td className="text-danger">{p.turnoversLost}</td>
                                      <td className="fw-bold">{p.netTurnovers > 0 ? `+${p.netTurnovers}` : p.netTurnovers}</td>
                                      <td>{p.safePlayPct}%</td>
                                      <td>
                                        <button
                                          type="button"
                                          className={`btn btn-xs ${selectedPlayerMath?.playerId === p.playerId ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-2 py-1`}
                                          style={{ fontSize: "11px", whiteSpace: "nowrap" }}
                                          onClick={() => setSelectedPlayerMath(selectedPlayerMath?.playerId === p.playerId ? null : p)}
                                        >
                                          <i className="ri-calculator-line me-1"></i>
                                          {selectedPlayerMath?.playerId === p.playerId ? 'Hide Math' : 'View Math'}
                                        </button>
                                      </td>
                                    </tr>
                                  ))
                              ) : (
                                <tr>
                                  <td colSpan="18" className="text-center py-3 text-muted">No player reports recorded for this match.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Interactive Live Formula Justification Box per Selected Player */}
                        {selectedPlayerMath && (
                          <div className="card border-0 shadow-sm rounded-3 mt-3 mb-2" style={{ backgroundColor: "#f0f9ff", borderLeft: "4px solid #0284c7" }}>
                            <div className="card-body p-4">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                  <h6 className="fw-bold mb-1 text-dark" style={{ fontSize: "16px" }}>
                                    🧮 Live Formula & Position Justification — {selectedPlayerMath.playerName}
                                  </h6>
                                  <span className="badge bg-secondary me-2">Court Position: {selectedPlayerMath.position}</span>
                                  <span className="badge bg-primary-light text-primary">Team: {selectedPlayerMath.teamName}</span>
                                </div>
                                <button type="button" className="btn-close" onClick={() => setSelectedPlayerMath(null)}></button>
                              </div>

                              {(() => {
                                const p = selectedPlayerMath;
                                const pos = p.position || "C";
                                const group = ["GS", "GA"].includes(pos) ? "SHOOTERS" : (["GD", "GK"].includes(pos) ? "DEFENDERS" : "MIDCOURT");
                                const goalWt = group === "SHOOTERS" ? 1.0 : (group === "MIDCOURT" ? 1.0 : 0.0);
                                const assistWt = 1.5;
                                const intWt = 1.5;
                                const tipWt = 0.75;
                                const rebWt = 0.75;
                                const toLostWt = -1.0;

                                const goalPts = (p.goals || 0) * goalWt;
                                const assistPts = (p.assists || 0) * assistWt;
                                const intPts = (p.interceptions || 0) * intWt;
                                const tipPts = (p.tips || 0) * tipWt;
                                const rebPts = (p.rebounds || 0) * rebWt;
                                const toLostPts = (p.turnoversLost || 0) * toLostWt;

                                return (
                                  <div className="row g-3">
                                    <div className="col-md-6">
                                      <div className="p-3 bg-white rounded-3 border h-100">
                                        <strong className="d-block text-primary mb-2" style={{ fontSize: "14px" }}>
                                          1. Court Position & Dynamic Weights ({group})
                                        </strong>
                                        <ul className="list-unstyled mb-0 small text-muted">
                                          <li className="mb-1">• <strong>Assigned Court Position:</strong> <code>{pos}</code> ({group} Position Group)</li>
                                          <li className="mb-1">• <strong>Goal Weight:</strong> <code>{goalWt} pts per Goal</code></li>
                                          <li className="mb-1">• <strong>Assist Weight:</strong> <code>{assistWt} pts per Assist</code></li>
                                          <li className="mb-1">• <strong>Interception Weight:</strong> <code>{intWt} pts per Interception</code></li>
                                          <li className="mb-1">• <strong>Tip / Deflection Weight:</strong> <code>{tipWt} pts per Touch</code></li>
                                          <li className="mb-1">• <strong>Rebound Weight:</strong> <code>{rebWt} pts per Rebound</code></li>
                                          <li className="mb-1">• <strong>Turnover Lost Penalty:</strong> <code>{toLostWt} pts per Lost Possession</code></li>
                                        </ul>
                                      </div>
                                    </div>

                                    <div className="col-md-6">
                                      <div className="p-3 bg-white rounded-3 border h-100">
                                        <strong className="d-block text-success mb-2" style={{ fontSize: "14px" }}>
                                          2. Step-by-Step Player Impact Rating (PIR) Math
                                        </strong>
                                        <div className="small">
                                          <code className="d-block mb-2 text-dark bg-light p-2 rounded">
                                            PIR = ({p.goals} Goals × {goalWt}) + ({p.assists} Assists × {assistWt}) + ({p.interceptions} INTs × {intWt}) + ({p.tips} Tips × {tipWt}) + ({p.rebounds} REBs × {rebWt}) + ({p.turnoversLost} TO Lost × {toLostWt})
                                          </code>
                                          <div className="mt-2 text-muted">
                                            Substituted Arithmetic: <code>{goalPts.toFixed(2)} + {assistPts.toFixed(2)} + {intPts.toFixed(2)} + {tipPts.toFixed(2)} + {rebPts.toFixed(2)} - {Math.abs(toLostPts).toFixed(2)}</code>
                                          </div>
                                          <div className="mt-2 text-success fw-bold p-2 bg-success-light rounded" style={{ fontSize: "15px" }}>
                                            ➡ Calculated PIR: {p.pir} pts
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="col-md-12">
                                      <div className="p-3 bg-white rounded-3 border">
                                        <strong className="d-block text-info mb-2" style={{ fontSize: "14px" }}>
                                          3. Individual Shooting, Feed & Safe Play Metrics
                                        </strong>
                                        <div className="row g-3 small">
                                          <div className="col-md-4">
                                            <div className="p-2 bg-light rounded">
                                              <strong>Shooting Efficiency Math:</strong><br />
                                              <code>({p.goals} Goals / {p.totalShots} Total Shots) × 100 = <strong>{p.shootingEfficiencyPct}%</strong></code>
                                            </div>
                                          </div>
                                          <div className="col-md-4">
                                            <div className="p-2 bg-light rounded">
                                              <strong>Feed Success Accuracy Math:</strong><br />
                                              <code>({p.feedSuccess} Success / {p.feeds} Total Feeds) × 100 = <strong>{p.feedSuccessRatePct}%</strong></code>
                                            </div>
                                          </div>
                                          <div className="col-md-4">
                                            <div className="p-2 bg-light rounded">
                                              <strong>Safe Play Retention Math:</strong><br />
                                              <code>[1 - ({p.turnoversLost} Lost / ({(p.goals || 0) + (p.turnoversLost || 0)} Total))] × 100 = <strong>{p.safePlayPct}%</strong></code>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ================= 3. MATCH EVENT LOGS ================= */}
                      <div className="mb-5">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <div>
                            <h6 className="main-content-label text-primary" style={{ fontSize: "16px" }}>
                              <i className="ri-history-line me-2"></i> 3. All Event Logs of Match
                            </h6>
                            <p className="text-muted small mb-0">Complete raw event logs recorded during match scoring.</p>
                          </div>
                          <div style={{ width: "250px" }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Filter action type or player..."
                              value={searchEvent}
                              onChange={(e) => setSearchEvent(e.target.value)}
                            />
                          </div>
                        </div>

                        <div style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid #e8e8f7", borderRadius: "8px" }}>
                          <table className="table table-bordered table-hover mb-0" style={{ fontSize: "13px" }}>
                            <thead className="bg-light text-dark position-sticky top-0" style={{ zIndex: 1 }}>
                              <tr>
                                <th>#</th>
                                <th>Quarter</th>
                                <th>Time Recorded</th>
                                <th>Player Name</th>
                                <th>Team</th>
                                <th>Position</th>
                                <th>Action Type</th>
                                <th>Points</th>
                                <th>Chain ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {matchAnalysisData?.eventLogs && matchAnalysisData.eventLogs.length > 0 ? (
                                matchAnalysisData.eventLogs
                                  .filter(e => 
                                    !searchEvent || 
                                    e.actionType.toLowerCase().includes(searchEvent.toLowerCase()) || 
                                    e.playerName.toLowerCase().includes(searchEvent.toLowerCase()) ||
                                    e.teamName.toLowerCase().includes(searchEvent.toLowerCase())
                                  )
                                  .map((evt, index) => (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td><span className="badge bg-dark">Q{evt.quarter}</span></td>
                                      <td>
                                        {evt.createdAt 
                                          ? new Date(evt.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }).toUpperCase()
                                          : "-"}
                                      </td>
                                      <td className="fw-semibold">{evt.playerName}</td>
                                      <td>{evt.teamName}</td>
                                      <td><span className="badge bg-light text-dark border">{evt.position}</span></td>
                                      <td>
                                        <span className={`badge ${evt.actionType.includes('GOAL') ? 'bg-success' : evt.actionType.includes('TURNOVER') ? 'bg-danger' : 'bg-info'} px-2 py-1`}>
                                          {evt.actionType.replace(/-/g, " ")}
                                        </span>
                                      </td>
                                      <td className="fw-bold">{evt.points}</td>
                                      <td><code style={{ fontSize: "11px" }}>{evt.chainId}</code></td>
                                    </tr>
                                  ))
                              ) : (
                                <tr>
                                  <td colSpan="9" className="text-center py-4 text-muted">No raw events recorded for this match.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* ================= 4. WRITTEN CALCULATIONS & FORMULAS ================= */}
                      <div className="card shadow-sm border-0 rounded-3 mb-4" style={{ backgroundColor: "#f4f7fb" }}>
                        <div className="card-body p-4">
                          <h6 className="main-content-label text-success mb-3" style={{ fontSize: "17px", textTransform: "none" }}>
                            <i className="ri-function-line me-2"></i> 4. Written Calculations, Formulas & Data Lineage Guide
                          </h6>
                          <p className="text-muted small mb-4">
                            {matchAnalysisData?.formulasAndSources?.description || "Mathematical formulas and data origins for all statistics calculated in the match analysis."}
                          </p>

                          <div className="alert alert-info border-0 rounded-3 mb-4">
                            <strong><i className="ri-node-tree me-1"></i> Data Lineage & Flow:</strong> {matchAnalysisData?.formulasAndSources?.dataLineage}
                          </div>
                        {/* Extract live match metrics for formula justification */}
                          {(() => {
                            const home = matchAnalysisData?.teamInsights?.homeTeamStats || {};
                            const away = matchAnalysisData?.teamInsights?.awayTeamStats || {};
                            const chains = matchAnalysisData?.teamInsights?.chains || {};

                            const homeDefScore = Number(home.defensivePoints ?? home.defensiveScore ?? ((home.interceptions || 0) + (home.tips || 0) + (home.rebounds || 0)));
                            const rawINTs = Math.round((home.interceptions || 0) / 1.5);
                            const rawTips = Math.round((home.tips || 0) / 0.75);
                            const rawREBs = Math.round((home.rebounds || 0) / 0.75);

                            const homeShootAcc = home.total_shots > 0 ? ((home.goals / home.total_shots) * 100).toFixed(1) : "0.0";
                            const awayShootAcc = away.total_shots > 0 ? ((away.goals / away.total_shots) * 100).toFixed(1) : "0.0";
                            const homeTotalFeeds = (home.feeds || 0) + (home.feeds_success || 0);
                            const homeFeedRate = homeTotalFeeds > 0 ? ((home.feeds_success / homeTotalFeeds) * 100).toFixed(1) : "0.0";
                            const homeTotalPoss = (home.goals || 0) + (home.turnovers_lost || 0);
                            const homeSafePlay = homeTotalPoss > 0 ? ((1 - ((home.turnovers_lost || 0) / homeTotalPoss)) * 100).toFixed(1) : "100.0";
                            const homeNetTO = (home.turnovers_won || 0) - (home.turnovers_lost || 0);

                            return (
                              <div className="row">
                                {/* Overview & PIR */}
                                <div className="col-md-6 mb-4">
                                  <div className="card border-0 shadow-xs h-100 p-3" style={{ borderLeft: "4px solid #008080" }}>
                                    <h6 className="fw-bold text-dark mb-2">1. Overview & Player Impact Rating (PIR)</h6>
                                    <p className="mb-2"><code>PIR = ∑ (Action Count × Position Weight)</code></p>
                                    <div className="bg-light p-2 rounded small mb-2">
                                      <strong className="text-primary">Live Match Substitution:</strong><br />
                                      • Home PIR Total: <span className="fw-bold text-success">{home.pir_total ?? 0}</span> | Away PIR Total: <span className="fw-bold text-info">{away.pir_total ?? 0}</span><br />
                                      • Defensive Score: <code>({rawINTs} INTs × 1.5) + ({rawTips} Tips × 0.75) + ({rawREBs} REB × 0.75) = <strong>{homeDefScore.toFixed(2)} pts</strong></code>
                                    </div>
                                    <div className="extra-small text-muted">
                                      <strong>Data Source:</strong> <code>MatchAction</code> logs filtered by playerId & position. Dynamic weights from <code>ExtraMatchActions</code> table.
                                    </div>
                                  </div>
                                </div>

                                {/* Shooting Tab */}
                                <div className="col-md-6 mb-4">
                                  <div className="card border-0 shadow-xs h-100 p-3" style={{ borderLeft: "4px solid #28a745" }}>
                                    <h6 className="fw-bold text-dark mb-2">2. Shooting Tab Formulas</h6>
                                    <p className="mb-2"><code>Shooting Efficiency % = (Total Goals / Total Shots) × 100</code></p>
                                    <div className="bg-light p-2 rounded small mb-2">
                                      <strong className="text-success">Live Match Substitution:</strong><br />
                                      • Home: <code>({home.goals || 0} Goals / {home.total_shots || 0} Shots) × 100 = <strong>{homeShootAcc}%</strong></code><br />
                                      • Away: <code>({away.goals || 0} Goals / {away.total_shots || 0} Shots) × 100 = <strong>{awayShootAcc}%</strong></code>
                                    </div>
                                    <div className="extra-small text-muted">
                                      <strong>Data Source:</strong> Filtered <code>GOAL</code> and <code>MISSED_SHOT</code> actions.
                                    </div>
                                  </div>
                                </div>

                                {/* Defence Tab */}
                                <div className="col-md-6 mb-4">
                                  <div className="card border-0 shadow-xs h-100 p-3" style={{ borderLeft: "4px solid #dc3545" }}>
                                    <h6 className="fw-bold text-dark mb-2">3. Defence Tab Formulas</h6>
                                    <p className="mb-2"><code>Defensive Impact = (INTs × 1.5) + (Tips × 0.75) + (REBs × 0.75)</code></p>
                                    <div className="bg-light p-2 rounded small mb-2">
                                      <strong className="text-danger">Live Match Substitution:</strong><br />
                                      • Interceptions: <code>{rawINTs} INTs × 1.5 = {(home.interceptions || 0).toFixed(2)} pts</code><br />
                                      • Tips/Deflections: <code>{rawTips} Tips × 0.75 = {(home.tips || 0).toFixed(2)} pts</code><br />
                                      • Rebounds: <code>{rawREBs} REBs × 0.75 = {(home.rebounds || 0).toFixed(2)} pts</code><br />
                                      • <strong>Total Defensive Score: {homeDefScore.toFixed(2)} pts</strong>
                                    </div>
                                    <div className="extra-small text-muted">
                                      <strong>Data Source:</strong> Defensive disruptor events from <code>MatchAction</code> logs.
                                    </div>
                                  </div>
                                </div>

                                {/* Possession Tab */}
                                <div className="col-md-6 mb-4">
                                  <div className="card border-0 shadow-xs h-100 p-3" style={{ borderLeft: "4px solid #17a2b8" }}>
                                    <h6 className="fw-bold text-dark mb-2">4. Possession & Safe Play Formulas</h6>
                                    <p className="mb-2"><code>Safe Play % = [1 - (Turnovers Lost / (Goals + Turnovers Lost))] × 100</code></p>
                                    <div className="bg-light p-2 rounded small mb-2">
                                      <strong className="text-info">Live Match Substitution:</strong><br />
                                      • Safe Play: <code>[1 - ({home.turnovers_lost || 0} Lost / ({home.goals || 0} Goals + {home.turnovers_lost || 0} Lost))] × 100 = <strong>{homeSafePlay}%</strong></code><br />
                                      • Feed Success: <code>({home.feeds_success || 0} Success / ({home.feeds || 0} Feeds + {home.feeds_success || 0} Success)) × 100 = <strong>{homeFeedRate}%</strong></code><br />
                                      • Net Turnovers: <code>{home.turnovers_won || 0} Won - {home.turnovers_lost || 0} Lost = <strong>{homeNetTO > 0 ? "+" : ""}{homeNetTO}</strong></code>
                                    </div>
                                    <div className="extra-small text-muted">
                                      <strong>Data Source:</strong> <code>TURNOVER_LOST</code>, <code>TURNOVER_WON</code>, <code>FEED_SUCCESS</code> actions.
                                    </div>
                                  </div>
                                </div>

                                {/* Pressure Tab */}
                                <div className="col-md-6 mb-4">
                                  <div className="card border-0 shadow-xs h-100 p-3" style={{ borderLeft: "4px solid #ffc107" }}>
                                    <h6 className="fw-bold text-dark mb-2">5. Pressure Performance Formulas</h6>
                                    <p className="mb-2"><code>Pressure Window = Last 20% of Quarter OR Score Difference ≤ 3 Goals</code></p>
                                    <div className="bg-light p-2 rounded small mb-2">
                                      <strong className="text-warning">Live Match Status:</strong><br />
                                      • Match Score Diff: <code>{Math.abs((home.score || 0) - (away.score || 0))} goals</code> ({Math.abs((home.score || 0) - (away.score || 0)) <= 3 ? "High Pressure Active" : "Standard Play"})<br />
                                      • High-Pressure Actions Filter: Calculated dynamically when score margin is ≤ 3 goals.
                                    </div>
                                    <div className="extra-small text-muted">
                                      <strong>Data Source:</strong> Events logged during clutch score windows.
                                    </div>
                                  </div>
                                </div>

                                {/* Quarters Tab */}
                                <div className="col-md-6 mb-4">
                                  <div className="card border-0 shadow-xs h-100 p-3" style={{ borderLeft: "4px solid #6f42c1" }}>
                                    <h6 className="fw-bold text-dark mb-2">6. Quarters Breakdown Formulas</h6>
                                    <p className="mb-2"><code>Quarter Goal Diff = Goals Scored - Goals Conceded</code></p>
                                    <div className="bg-light p-2 rounded small mb-2">
                                      <strong style={{ color: "#6f42c1" }}>Live Match Breakdown:</strong><br />
                                      • Total Match Goals: <code>{home.goals || 0} Goals Scored vs {away.goals || 0} Conceded</code><br />
                                      • Overall Differential: <code>{(home.goals || 0) - (away.goals || 0) > 0 ? "+" : ""}{(home.goals || 0) - (away.goals || 0)} Goal Diff</code>
                                    </div>
                                    <div className="extra-small text-muted">
                                      <strong>Data Source:</strong> Aggregated actions grouped by <code>quarter</code>.
                                    </div>
                                  </div>
                                </div>

                                {/* Chains Tab */}
                                <div className="col-md-6 mb-4">
                                  <div className="card border-0 shadow-xs h-100 p-3" style={{ borderLeft: "4px solid #6c757d" }}>
                                    <h6 className="fw-bold text-dark mb-2">7. Conversion Chains (11-Field Complete Formula Breakdown)</h6>
                                    <p className="mb-2"><code>Conversion Rate % = (Converted Goal Chains / Total Possessions) × 100</code></p>
                                    <div className="bg-light p-2 rounded small mb-2">
                                      <strong className="text-secondary d-block mb-1">Live Match Field-by-Field Formula Substitution:</strong>
                                      <div className="extra-small">
                                        • <code>cpTotal</code> (Total Center Passes): <strong>{chains.cpTotal || 0}</strong><br />
                                        • <code>cpGoals</code> (Center Pass Goals): <strong>{chains.cpGoals || 0}</strong><br />
                                        • <code>cpShots</code> (Center Pass Shots): <strong>{chains.cpShots || 0}</strong><br />
                                        • <code>cpRate</code>: <code>({chains.cpGoals || 0} / {chains.cpTotal || 0}) × 100 = <strong>{chains.cpRate || 0}%</strong></code><br />
                                        • <code>cpShotsRate</code>: <code>({chains.cpShots || 0} / {chains.cpTotal || 0}) × 100 = <strong>{chains.cpShotsRate || 0}%</strong></code><br />
                                        • <code>gainTotal</code> (Total Gains): <strong>{chains.gainTotal || 0}</strong><br />
                                        • <code>gainGoals</code> (Gain Goals): <strong>{chains.gainGoals || 0}</strong><br />
                                        • <code>gainRate</code>: <code>({chains.gainGoals || 0} / {chains.gainTotal || 0}) × 100 = <strong>{chains.gainRate || 0}%</strong></code><br />
                                        • <code>feedTotal</code> (Total Feeds): <strong>{chains.feedTotal || 0}</strong><br />
                                        • <code>feedGoals</code> (Feed Goals): <strong>{chains.feedGoals || 0}</strong><br />
                                        • <code>feedRate</code>: <code>({chains.feedGoals || 0} / {chains.feedTotal || 0}) × 100 = <strong>{chains.feedRate || 0}%</strong></code>
                                      </div>
                                    </div>
                                    <div className="extra-small text-muted">
                                      <strong>Data Source:</strong> Sequenced possession chain IDs tracking Center Pass, Gain, and Circle Feeds to Goal/Shot.
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-muted">No match analysis data found for this match.</p>
                    </div>
                  )}

                  {/* BACK BUTTON */}
                  <div className="mt-4">
                    <button
                      className="btn ripple btn-dark"
                      type="button"
                      onClick={() => navigate(-1)}
                    >
                      <i className="ace-icon-solid ion-arrow-return-left bigger-110 mx-1"></i>
                      {t("btn_back")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MatchManagamentDetails;